import { motion } from 'framer-motion';
import Layout from '../components/Layout';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';

const RefundPage = () => {
  return (
    <Layout>
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-50 via-rose-50 to-slate-50 py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <Badge className="mb-4">Legal</Badge>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4 text-slate-900">
              Refund & <span className="text-primary">Cancellation Policy</span>
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Please read our refund and cancellation policies carefully.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="border-0 shadow-lg bg-slate-50/50 p-6 md:p-10">
              <CardContent className="prose prose-slate max-w-none space-y-8">
                <div>
                  <h2 className="font-heading text-2xl font-bold text-slate-900 mb-3">Refund Policy</h2>
                  <p className="text-slate-600 leading-relaxed">
                    This Refund & Cancellation Policy applies to the educational services provided by Caliber Nova through its website, admission process, and coaching programs.
                  </p>
                </div>

                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Eligibility for Refund</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Refunds may be considered under the following conditions:
                  </p>
                  <ul className="list-disc pl-6 text-slate-600 space-y-1 mt-2">
                    <li><strong>Full Refund:</strong> If Caliber Nova cancels a course, batch, or program for any reason.</li>
                    <li><strong>Limited Refund:</strong> If a student requests cancellation within 7 days of enrollment and before the commencement of classes, a refund may be considered after deducting applicable registration and administrative charges.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Non-Refundable Cases</h3>
                  <p className="text-slate-600 leading-relaxed">
                    No refunds will be issued if:
                  </p>
                  <ul className="list-disc pl-6 text-slate-600 space-y-1 mt-2">
                    <li>The course or batch has already commenced.</li>
                    <li>Study materials (physical or digital) have been provided or accessed.</li>
                    <li>The refund request is submitted after the 7-day eligibility period.</li>
                    <li>The student discontinues attending classes after enrollment.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Refund Request Process</h3>
                  <p className="text-slate-600 leading-relaxed">
                    To request a refund, students or parents must email us at:
                  </p>
                  <p className="text-slate-700 font-semibold mt-2">
                    Email: <a href="mailto:info@calibernova.in" className="text-primary hover:underline">info@calibernova.in</a>
                  </p>
                  <p className="text-slate-600 mt-4 font-medium">
                    Please include the following details in your email:
                  </p>
                  <ul className="list-disc pl-6 text-slate-600 space-y-1 mt-1">
                    <li>Student Name</li>
                    <li>Course Name</li>
                    <li>Enrollment Date</li>
                    <li>Contact Number</li>
                    <li>Reason for Refund Request</li>
                  </ul>
                  <p className="text-slate-500 italic mt-2 text-sm">
                    *Requests submitted through WhatsApp, phone calls, or social media may be redirected to email for proper documentation and processing.
                  </p>
                </div>

                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Refund Processing</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Approved refunds will be processed through the original mode of payment. 
                    Refund processing time is generally 10–15 business days. 
                    Any applicable bank, payment gateway, or transaction charges may be deducted from the refundable amount.
                  </p>
                </div>

                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Course Modifications</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Caliber Nova reserves the right to modify, reschedule, merge, postpone, or cancel any course, batch, faculty assignment, or class schedule due to academic or operational requirements. 
                    In such cases, eligible refunds, if applicable, will be processed accordingly.
                  </p>
                </div>

                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Policy Updates</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Caliber Nova may update this Refund Policy from time to time. 
                    Continued use of our services indicates acceptance of the revised policy.
                  </p>
                </div>

                <div className="pt-6 border-t border-slate-200">
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Contact Information</h3>
                  <p className="text-slate-700 font-semibold">Caliber Nova</p>
                  <ul className="space-y-1 text-slate-600 text-sm mt-2">
                    <li><strong>Email:</strong> info@calibernova.in</li>
                    <li><strong>Website:</strong> www.calibernova.in</li>
                    <li><strong>Address:</strong> 2nd Floor, Zenda Chowk, Mahal, Nagpur - 440032</li>
                    <li><strong>Phone:</strong> +91 9595253778 | +91 9527225500</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default RefundPage;
